import { all, apply, fork, put, takeEvery } from "redux-saga/effects";
import { IApiResponse } from "src/common/ApiResponse";
import contactUsService from "src/dataServices/ContactUsService";
import { feedbackService } from "src/dataServices/FeedbackService";
import ResourceService from "src/dataServices/ResourceService";
import { supportTelemetryService } from "src/dataServices/SupportTelemetryService";
import { ActionType } from "typesafe-actions";
import { NonprofitError } from "../nonprofit";
import { ICountryCode } from "../shared";
import {
  contactusResourcesError,
  contactusResourcesSuccess,
  createSupportTicket,
  createSupportTicketError,
  createSupportTicketSuccess
} from "./actions";
import { ContactUsActionTypes, IAgentSupportTicketMetadata, ISupportTicketResponse } from "./types";

const isAgentSupportTicketMetadata = (metadata: unknown): metadata is IAgentSupportTicketMetadata =>
  typeof metadata === "object" && metadata !== null && "source" in metadata && metadata.source === "agent";

// The support API returns ASP.NET ModelState at the response root — `{ UserEmail: ["..."], ... }` —
// rather than nesting it under ValidationProblemDetails. Plain-string or malformed bodies are also possible.
export const parseSupportTicketFieldErrors = (error: unknown): Record<string, string> | undefined => {
  if (typeof error !== "object" || error === null || Array.isArray(error)) {
    return undefined;
  }

  const fieldErrors: Record<string, string> = {};
  Object.entries(error).forEach(([field, messages]) => {
    const message = (Array.isArray(messages) ? messages : [messages])
      .filter((entry: unknown): entry is string => typeof entry === "string" && entry.trim().length > 0)
      .join(" ");

    if (field && message) {
      fieldErrors[field] = message;
    }
  });

  return Object.keys(fieldErrors).length > 0 ? fieldErrors : undefined;
};

export function* handleGetContactusResourcesRequest() {
  try {
    const countriesResponse: IApiResponse<ICountryCode[]> = yield apply(
      ResourceService,
      ResourceService.getCountryCodes,
      []
    );

    if (countriesResponse.status === 200) {
      yield put(contactusResourcesSuccess(countriesResponse.value));
    } else {
      const error = "Error in fetching resources for Contact Us from API.";
      console.error(error);
      yield errorAndRedirect(NonprofitError.Unknown);
    }
  } catch (error) {
    console.error("Error in fetching resources for Contact Us from API.", error);
    yield errorAndRedirect(NonprofitError.Unknown);
  }
}

export function* handleCreateSupportTicket(action: ActionType<typeof createSupportTicket>) {
  try {
    const response: IApiResponse<ISupportTicketResponse> = yield apply(
      contactUsService,
      contactUsService.CreateSupportTicket,
      [action.payload]
    );
    if (response.status === 200) {
      yield put(createSupportTicketSuccess(response.value, action.payload.userEmail));
      const metadata = "meta" in action ? action.meta : undefined;
      if (isAgentSupportTicketMetadata(metadata)) {
        supportTelemetryService.trackAgentTicketCreated({
          topic: action.payload.category,
          description: action.payload.issue,
          aiGeneratedTopic: metadata.aiGeneratedTopic,
          aiGeneratedDescription: metadata.aiGeneratedDescription,
          turnCountBeforeTicket: metadata.turnCountBeforeTicket,
          role: action.payload.role,
          threadId: metadata.threadId
        });
        void feedbackService.logSupportTicketEvent({
          threadId: metadata.threadId,
          outcome: "Submitted",
          category: action.payload.category,
          role: action.payload.role
        });
      }
    } else {
      console.error("Error occured while creating support ticket");
      const fieldErrors = response.status === 400 ? parseSupportTicketFieldErrors(response.error) : undefined;
      yield put(createSupportTicketError(fieldErrors));
    }
  } catch (error) {
    console.error("Error occured while creating support ticket");
    yield put(createSupportTicketError());
  }
}

function* errorAndRedirect(error: NonprofitError) {
  yield all([put(contactusResourcesError(error))]);
}
function* watchCreateSupportTicket() {
  yield takeEvery(ContactUsActionTypes.CONTACTUS_CREATESUPPORTTICKET, handleCreateSupportTicket);
}
function* watchContactUsResourcesRequest() {
  yield takeEvery(ContactUsActionTypes.CONTACT_RESOURCES_REQUEST, handleGetContactusResourcesRequest);
}
function* ContactUsSaga() {
  yield all([fork(watchCreateSupportTicket), fork(watchContactUsResourcesRequest)]);
}

export default ContactUsSaga;
